<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('works.work_group_list.name')"
      :grid-ref="$refs[workGroupGridRefKey]"
      :show-add="false"
      :show-filter="false"
    />
    <div class="content-body pt-1">
      <div class="row">
        <div id="toast-msg"></div>
      </div>
      <DxDataGrid
        :ref="workGroupGridRefKey"
          :data-source="workDatasource"
          :column-min-width="110"
          style="min-height: 350px"
          :show-column-lines="true"
          :show-borders="true"
          :column-auto-width="true"
          :allowColumnReordering="true"
          :allowColumnResizing="true"
          :row-alternation-enabled="true"
          :remote-operations="gridRemoteOperations"
          :element-attr="gridAttributes"
          :allow-column-resizing="false"
          :on-row-prepared="rowMarker"
          @editing-start="onEditingStart"
          @rowRemoving="onRowRemoving"
          @option-changed="onOptionChanged"
        >
          <DxColumn
            data-field="name"
            data-type="string"
            :caption="$t('common.name')"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            data-field="main_group_name"
            data-type="string"
            :caption="$t('works.work_group_list.main_group')"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            data-field="last_year_unit_price"
            data-type="number"
            :caption="$t('common.unit_price') + ' / ha'"
            :format="this.getDxGridDefaultNumberFormat"
            :allow-editing="false"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
          >
          </DxColumn>
          <DxColumn
            dataField="updated_at"
            :caption="$t('common.last_update')"
            :filter-operations="['contains']"
            :allowHeaderFiltering="false"
            :allowSorting="false"
            width="15%"
            data-type="date"
            :format="this.getDxGridDefaultDateFormat + ' HH:mm'"
          />
          <DxColumn
            :width="60"
            alignment="center"
            :fixed="true"
            fixed-position="right"
            :allow-sorting="false"
            :allow-filtering="false"
            :show-in-column-chooser="false"
            cell-template="dynamicMenuCell"
          />
          <template #dynamicMenuCell="{ data }">
            <DxActionMenu
              :grid-ref="$refs[workGroupGridRefKey]"
              row-key="uuid"
              :row="data.data"
              :can-edit="canEdit"
              :can-delete="canDelete"
              :allow-deleting="allowDeleting"
              :items="getIntervalPriceMenuItems(data.data)"
              :menu-width="210"
            />
          </template>
          <DxPaging :total="3500" :page-size="15" />
          <DxPager
            :visible="true"
            :allowed-page-sizes="pageSizes"
            display-mode="compact"
            :show-page-size-selector="true"
            :show-info="true"
            :show-navigation-buttons="true"
            :info-text="$t('common.page_info')"
          />
          <DxEditing :allow-adding="false" :allow-updating="false" :allow-deleting="false" :use-icons="true" mode="row">
            <DxTexts confirm-delete-message="Sigur vreți să ștergeți lucrarea?" , confirm-delete-title="Confirmare ștergere" />
          </DxEditing>
          <DxHeaderFilter :visible="true" />
          <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
          <DxSelection mode="single" />
          <DxScrolling column-rendering-mode="virtual" />
          <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
          <DxColumnChooser :enabled="false" mode="select" />
      </DxDataGrid>
      <WorkGroupEdit
        v-if="editingPopupVisible"
        :workGroupData="editingRowData"
        :visible="editingPopupVisible"
        @close="
          editingPopupVisible = false;
          editingRowData = null;
        "
        @save="
          editingPopupVisible = false;
          editingRowData = null;
          $refs[workGroupGridRefKey].instance.refresh();
        "
      />
      <WorkDateIntervalUnitPricePopup
        v-if="intervalPricePopupVisible && intervalPriceWork"
        :visible="intervalPricePopupVisible"
        :work="intervalPriceWork"
        @close="closeIntervalPricePopup"
        @save="closeIntervalPricePopup"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  DxColumn,
  DxDataGrid,
  DxEditing,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging,
  DxSelection,
  DxTexts,
  DxScrolling,
  DxGroupPanel,
  DxColumnChooser,
} from "devextreme-vue/data-grid";
import { actions, canView, canDelete, canEdit, hasPermission, permissions, permissionTypes } from "~/helpers/Permissions";
import CustomStore from "devextreme/data/custom_store";
import notify from "devextreme/ui/notify";
import WorkGroupEdit from "./WorkGroupEdit.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import WorkDateIntervalUnitPricePopup from "./WorkDateIntervalUnitPricePopup.vue";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";

export default {
  name: "WorkGroupList",
  middleware: ["auth", "check-company-admin"],
  data() {
    return {
      seasonsList: [],
      gridAttributes: {
        class: "smagria-grid work-group-list-grid",
      },
      workGroupGridRefKey: "workGroupListGrid",
      gridRemoteOperations: {
        paging: false,
        filtering: false,
        sorting: false,
      },
      pageSizes: [15, 50, 100],
      workDatasource: new CustomStore({
        key: "uuid",
        load: this.load,
        remove: this.remove,
      }),
      editingPopupVisible: false,
      editingRowData: null,
      intervalPricePopupVisible: false,
      intervalPriceWork: null,
    };
  },
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxEditing,
    DxTexts,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxScrolling,
    DxGroupPanel,
    DxColumnChooser,
    WorkGroupEdit,
    GridPanelHeader,
    DxActionMenu,
    WorkDateIntervalUnitPricePopup,
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
      getDxGridDefaultNumberFormat: "settings/getDxGridDefaultNumberFormat",
      user: "auth/user",
    }),
    config() {
      return {
        permission: permissions.WORK_GROUPS,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    canManageIntervalPrices() {
      return hasPermission({}, this.user, permissions.WORK_INTERVAL_UNIT_PRICES, actions.EDIT);
    },
  },
  methods: {
    ...mapActions({
      deleteWork: "works-manager/deleteWork",
      getWorkGroups: "works-manager/getWorks",
      getSeasons: "seasons/getSeasons",
    }),
    allowUpdating() {
      // Allow editing if the row is not deleted and has no stock movement
      return this.canEdit;
    },
    allowDeleting() {
      // Hide delete icon if deleted_at is set
      return false; // this.canDelete;
    },
    getIntervalPriceMenuItems(row) {
      if (!this.canManageIntervalPrices) return [];
      return [{
        key: "interval-unit-prices",
        label: this.$t("works.work_group_list.add_edit_unit_prices"),
        position: 1,
        icon: require("@/assets/images/icons/list.svg"),
        action: () => this.openIntervalPricePopup(row),
      }];
    },
    openIntervalPricePopup(row) {
      this.intervalPriceWork = row;
      this.intervalPricePopupVisible = true;
    },
    closeIntervalPricePopup() {
      this.intervalPricePopupVisible = false;
      this.intervalPriceWork = null;
    },
    onEditingStart(e) {
      e.cancel = true;
      this.editingRowData = e.data; // Store the row data for editing
      this.editingPopupVisible = true;
    },
    rowMarker(e) {
      if (e.rowType === "data" && (e.data.has_batch_item || e.data.has_stock_transaction)) {
        //e.rowElement.style.backgroundColor = ROW_BACKGROUND_COLOR;
      }
    },
    onRowRemoving(e) {
      e.cancel = true;
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        const instance = this.$refs?.[this.workGroupGridRefKey]?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
        }
      }
    },
    async load() {
      let dataSource = {
        data: [],
        totalCount: 0,
      };

      let payload = {
        limit: 99999,
      };

      await this.getWorkGroups(payload).then((resp) => {
        dataSource.data = resp.data;
        dataSource.totalCount = resp.pagination?.total ?? 1;
      });

      return dataSource;
    },
    async remove(key) {
      await this.deleteWork({
        uuid: key,
      })
        .then(() => {
          this.workNotify("Work has been deleted.", "success");
        })
        .catch(() => {
          this.workNotify("Error deleting work.", "error");
        });
    },
    workNotify(message, type) {
      notify(
        {
          message: message,
          width: "auto",
          position: {
            at: "center",
            my: "start",
            of: "#toast-msg",
          },
        },
        type,
        3000,
      );
    },
    onExporting(e) {
      e.fileName = "WorkGroups_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("WorkGroups");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
  },
  mounted() {
    if (!this.canView) {
      return;
    }

    this.getSeasons();
  },
};
</script>
